import camelcaseKeys from 'camelcase-keys'
import { SvelteDate, SvelteMap } from 'svelte/reactivity'
import { writable } from 'svelte/store'

import type { IStorageConfig } from '@routes/storage-configs/storage-configs-store'
import { handleFetch } from '@utils/fetch'

enum ModelSourceType {
  PYTORCH_RAW_PTH = 'pytorch_raw_pth',
  TORCHVISION = 'torchvision',
}

const modelSourceTypesValues = Object.values(ModelSourceType)
const displayModelSourceTypes = {
  [ModelSourceType.PYTORCH_RAW_PTH]: 'PyTorch raw .pth',
  [ModelSourceType.TORCHVISION]: 'Torchvision',
}

class PyTorchRawPthSource {
  type = ModelSourceType.PYTORCH_RAW_PTH

  public rawPthUrl = ''
  public codeUrl = ''

  toJSON() {
    return {
      rawPthUrl: this.rawPthUrl,
      codeUrl: this.codeUrl,
    }
  }
}

class TorchVisionSource {
  type = ModelSourceType.TORCHVISION

  public modelName = ''
  public safetensorsWeightsUrl = ''

  toJSON() {
    return {
      modelName: this.modelName,
      safetensorsWeightsUrl: this.safetensorsWeightsUrl,
    }
  }
}

type IModelSources = PyTorchRawPthSource | TorchVisionSource

const isPyTorchRawPthSource = (source: IModelSources): source is PyTorchRawPthSource => {
  return source.type === ModelSourceType.PYTORCH_RAW_PTH
}

const isTorchVisionSource = (source: IModelSources): source is TorchVisionSource => {
  return source.type === ModelSourceType.TORCHVISION
}

interface IModel {
  id: number

  storageConfigId: number
  storageConfig: IStorageConfig

  classMappingUrl: string | null
  classMappingJson?: Record<string, number> | Record<number, string> | string[] | null

  dataRootUrl: string

  organizationId?: number
  creatorId: number
  creatorName?: string
  createdAt: SvelteDate
  updatedAt: SvelteDate

  modelName: string
  modelSource: IModelSources
}

type IRawModel = Omit<IModel, 'createdAt' | 'updatedAt'> & {
  createdAt: string
  updatedAt: string
}

interface ICreateModel {
  storageConfigId: number
  classMappingUrl: string | null
  classMappingJson?: Record<string, number> | Record<number, string> | string[] | null
  dataRootUrl: string
  organizationId?: number
  modelName: string
  modelSource: IModelSources
  replaceIfExists?: boolean
  archiveExistingRuns?: boolean
}

const modelStore = writable(new SvelteMap<number, IModel>())

modelStore.subscribe((models) => {
  console.log('Models updated: ', models)
})

const parseModels = (rawDatasets: IRawModel): IModel => {
  const {
    id,
    storageConfigId,
    storageConfig,
    classMappingUrl,
    classMappingJson,
    dataRootUrl,
    organizationId,
    creatorId,
    creatorName,
    createdAt,
    updatedAt,
    modelName,
    modelSource,
  } = rawDatasets

  return {
    id,
    storageConfigId,
    storageConfig,
    classMappingUrl,
    classMappingJson,
    dataRootUrl,
    organizationId,
    creatorId,
    creatorName,
    createdAt: new SvelteDate(SvelteDate.parse(createdAt)),
    updatedAt: new SvelteDate(SvelteDate.parse(updatedAt)),
    modelName,
    modelSource,
  }
}

const modelAsyncState = writable({
  loading: false,
  error: '',
})

const loadModels = async () => {
  modelAsyncState.set({
    loading: true,
    error: '',
  })
  try {
    const raw = await (await handleFetch('/unlearning/ml-model/')).json()
    // Avoid deep camel-casing inside class_mapping_json to preserve user-provided keys
    const models: IRawModel[] = (Array.isArray(raw) ? raw : []).map((m) =>
      camelcaseKeys(m, { deep: true, stopPaths: ['class_mapping_json'] }),
    )
    const modelMap = models.reduce((acc, model) => {
      acc.set(model.id, parseModels(model))
      return acc
    }, new SvelteMap<number, IModel>())
    modelStore.set(modelMap)
    modelAsyncState.set({
      loading: false,
      error: '',
    })
  } catch (err) {
    console.error('Failed to load models: ', err)
    modelAsyncState.set({
      loading: false,
      error: 'Failed to load models',
    })
  }
}

export type { ICreateModel, IModel }
export {
  displayModelSourceTypes,
  isPyTorchRawPthSource,
  isTorchVisionSource,
  loadModels,
  modelAsyncState,
  ModelSourceType,
  modelSourceTypesValues,
  modelStore,
  parseModels,
  PyTorchRawPthSource,
  TorchVisionSource,
}
